<template>
    <div
        :class="{ hidden: !isOpen }"
        class="pdp-lightbox-slider fixed top-0 left-0 z-[60] h-[100dvh] w-full overflow-hidden bg-white"
        role="dialog"
        aria-modal="true"
        :aria-label="snippets?.['headless.pdp.lightboxLabel'] || 'Product image lightbox'"
        @keydown="handleLightboxKeydown">
        <div class="relative h-full">
            <div class="absolute top-5 right-5 flex justify-end lg:right-20">
                <button
                    @click="closeLightbox"
                    class="z-30 flex items-center leading-none font-bold focus:ring-0 focus:ring-offset-0 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.closeLightbox'] || 'Close lightbox'"
                    ref="closeButton">
                    {{ snippets['headless.pdp.closeLightbox'] }}
                    <XMarkIcon class="ml-3 size-5" />
                </button>
            </div>
            <Swiper
                class="relative mb-6 h-[calc(100dvh-160px)] !pt-8 !pb-4 lg:mb-0 lg:h-[calc(100dvh)] lg:!pt-[100px] lg:!pb-[100px]"
                @swiper="initializeMainSwiper"
                @slideChange="onLightboxSlideChange"
                @slideChangeTransitionStart="onLightboxSlideChangeTransitionStart"
                :modules="[
                    SwiperAutoplay,
                    SwiperFreeMode,
                    SwiperNavigation,
                    SwiperThumbs,
                    SwiperPagination,
                    SwiperKeyboard,
                ]"
                :keyboard="{
                    enabled: true,
                    onlyInViewport: false,
                }"
                :observer="true"
                :observeParents="true"
                :grabCursor="!isImageZoomed"
                :speed="300"
                :watchSlidesProgress="true"
                :allowTouchMove="!isImageZoomed"
                :pagination="{ clickable: true }"
                :navigation="{
                    nextEl: '.lightbox-slider-next',
                    prevEl: '.lightbox-slider-prev',
                }"
                :thumbs="{ swiper: thumbsSwiper }">
                <SwiperSlide
                    class="!flex h-full items-center justify-center"
                    v-for="(image, index) in [...(product?.media ?? [])].sort((a, b) => a.position - b.position)"
                    :key="image.id || image.mediaId || index">
                    <div
                        class="zoom-container flex h-full w-full items-center justify-center p-4 lg:p-0"
                        @touchstart.passive="handleTouchStart"
                        @touchmove.prevent="handleTouchMove"
                        @touchend.passive="handleTouchEnd"
                        @wheel.prevent="handleWheel"
                        @mousedown.prevent="handleMouseDown"
                        @dblclick.prevent="handleDoubleClick"
                        ref="zoomContainer"
                        :class="{
                            'cursor-grab': !isImageZoomed && !isUserPanning,
                            'cursor-grabbing': isUserPanning,
                            'cursor-move': isImageZoomed && !isUserPanning,
                        }">
                        <ShopwareImg
                            class="zoom-target h-full max-h-full w-full max-w-full object-cover lg:object-contain"
                            :class="{ 'transition-transform duration-200 ease-out': !isUserPanning }"
                            :src="image.media.url"
                            :alt="image.media.alt || product?.translated?.name || 'Product image'"
                            :width="1200"
                            :style="getImageTransformStyle(index)"
                            @load="handleImageLoad"
                            draggable="false"
                            :aria-label="`${snippets?.['headless.pdp.imageLabel'] || 'Product image'} ${index + 1} ${snippets?.['headless.pdp.of'] || 'of'} ${product?.media?.length || 1}. ${snippets?.['headless.pdp.zoomInstructions'] || 'Double click or pinch to zoom'}`" />
                    </div>
                </SwiperSlide>
            </Swiper>
            <div
                class="pointer-events-none absolute top-[50%] right-0 left-0 z-20 hidden translate-y-[-50%] justify-center gap-[50%] lg:flex xl:gap-[60%]"
                :class="{ 'opacity-50': isImageZoomed }">
                <button
                    class="lightbox-slider-prev pointer-events-auto border-2 border-black bg-white p-3 focus:ring-2 focus:ring-black focus:ring-offset-2 focus:outline-none"
                    :disabled="isImageZoomed"
                    :aria-label="snippets?.['headless.pdp.previousImage'] || 'Previous image'">
                    <NuxtImg
                        :alt="snippets['headless.pdp.previousImage']"
                        class="size-5 rotate-180"
                        src="/icons/arrow.svg" />
                </button>
                <button
                    class="lightbox-slider-next pointer-events-auto border-2 border-black bg-white p-3 focus:ring-2 focus:ring-black focus:ring-offset-2 focus:outline-none"
                    :disabled="isImageZoomed"
                    :aria-label="snippets?.['headless.pdp.nextImage'] || 'Next image'">
                    <NuxtImg :alt="snippets['headless.pdp.nextImage']" class="size-5" src="/icons/arrow.svg" />
                </button>
            </div>

            <div class="swiper-pagination block lg:hidden"></div>

            <!-- Desktop zoom controls -->
            <div
                class="absolute top-1/2 right-8 z-30 hidden -translate-y-1/2 transform flex-col gap-2 lg:flex"
                v-if="isImageZoomed">
                <button
                    @click="increaseZoomLevel"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-black/80 text-lg font-bold text-white backdrop-blur-sm transition-colors hover:bg-black focus:ring-2 focus:ring-white focus:ring-offset-2 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.zoomIn'] || 'Zoom in'">
                    +
                </button>
                <button
                    @click="resetCurrentSlideZoom"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-black/80 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-black focus:ring-2 focus:ring-white focus:ring-offset-2 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.resetZoom'] || 'Reset zoom'">
                    1:1
                </button>
                <button
                    @click="decreaseZoomLevel"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-black/80 text-lg font-bold text-white backdrop-blur-sm transition-colors hover:bg-black focus:ring-2 focus:ring-white focus:ring-offset-2 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.zoomOut'] || 'Zoom out'">
                    −
                </button>
            </div>

            <!-- Mobile zoom controls -->
            <div
                class="absolute top-1/3 right-5 z-30 flex -translate-y-1/2 transform flex-col gap-2 lg:hidden"
                v-if="isImageZoomed">
                <button
                    @click="increaseZoomLevel"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-black/70 text-lg font-bold text-white backdrop-blur-sm focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/70 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.zoomIn'] || 'Zoom in'">
                    +
                </button>
                <button
                    @click="resetCurrentSlideZoom"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-black/70 text-xs font-medium text-white backdrop-blur-sm focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/70 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.resetZoom'] || 'Reset zoom'">
                    1:1
                </button>
                <button
                    @click="decreaseZoomLevel"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-black/70 text-lg font-bold text-white backdrop-blur-sm focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/70 focus:outline-none"
                    :aria-label="snippets?.['headless.pdp.zoomOut'] || 'Zoom out'">
                    −
                </button>
            </div>

            <!-- Zoom level indicator -->
            <div
                v-if="isImageZoomed"
                class="absolute top-16 left-5 z-30 rounded-lg bg-black/70 px-3 py-1 text-sm font-medium text-white lg:top-5">
                {{ Math.round(currentZoomLevel * 100) }}%
                <div class="mt-1 text-xs opacity-75">
                    {{ snippets?.['headless.pdp.dragToPan'] || 'Drag to pan' }}
                </div>
            </div>

            <!-- Mobile instructions when not zoomed -->
            <div
                v-if="!isImageZoomed"
                class="absolute top-16 left-5 z-30 rounded-lg bg-black/70 px-3 py-1 text-xs font-medium text-white opacity-75 lg:hidden">
                {{ snippets?.['headless.pdp.doubleTapToZoom'] || 'Double tap to zoom' }}
            </div>
            <Swiper
                class="relative z-10 !mb-0 h-max !px-4 lg:!absolute lg:top-1/4 lg:left-20 lg:h-[600px] lg:w-[100px] lg:!px-0"
                @swiper="initializeThumbnailSwiper"
                :slidesPerView="4.5"
                :freeMode="true"
                :spaceBetween="10"
                :mousewheel="true"
                :watchSlidesProgress="true"
                :breakpoints="{
                    992: {
                        direction: 'vertical',
                    },
                }"
                :modules="[SwiperAutoplay, SwiperFreeMode, SwiperNavigation, SwiperThumbs, SwiperMousewheel]">
                <SwiperSlide
                    class="cursor-pointer"
                    v-for="(image, index) in [...(product?.media ?? [])].sort((a, b) => a.position - b.position)"
                    :key="image.id || image.mediaId || `thumb-${index}`">
                    <ShopwareImg
                        class="h-[110px] w-full object-cover lg:h-full"
                        :src="image.media.url"
                        :alt="image.media.alt || product?.translated?.name || 'Product image'"
                        :width="110" />
                </SwiperSlide>
            </Swiper>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { nextTick, ref, watch, toRef } from 'vue';
    import type { SwiperClass } from 'swiper/types';
    import {
        Navigation as SwiperNavigation,
        Pagination as SwiperPagination,
        Autoplay as SwiperAutoplay,
        FreeMode as SwiperFreeMode,
        Mousewheel as SwiperMousewheel,
        Thumbs as SwiperThumbs,
        Keyboard as SwiperKeyboard,
    } from 'swiper/modules';
    import { useSnippetsStore } from '~/stores/snippetsStore';
    import { XMarkIcon } from '@heroicons/vue/24/solid';
    import { useLightboxZoom } from '~/composables/useLightboxZoom';

    const props = defineProps<{
        product: any;
        isOpen: boolean;
        initialSlide: number;
    }>();

    const emit = defineEmits<{
        isOpen: [event: string];
        slideChange: [slide: number];
    }>();

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets as any;

    // GTM tracking setup
    const gtm = useGtm();

    // Use the lightbox zoom composable
    const {
        // Reactive state
        currentZoomLevel,
        isImageZoomed,
        isUserPanning,
        currentPanOffset,
        isRenderingInProgress,

        // Swiper refs (shared with composable)
        mainSwiper,
        thumbsSwiper,

        // Core functions
        initializeMainSwiper,
        initializeThumbnailSwiper,
        resetCurrentSlideZoom,
        resetAllZoomStates,
        increaseZoomLevel,
        decreaseZoomLevel,
        getImageTransformStyle,

        // Event handlers
        handleDoubleClick,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleWheel,
        handleKeydown,
        handleImageLoad,

        // Slide management
        onLightboxSlideChange: onZoomSlideChange,
        navigateToSlide,
        restoreSlideZoomState,

        // Browser state management
        preventBrowserZoom,
        restoreBrowserZoom,
        preventPageScrolling,
        restorePageScrolling,
    } = useLightboxZoom();

    // References
    const closeButton = ref<HTMLButtonElement | null>(null);
    const zoomContainer = ref<HTMLElement[]>([]);

    // Aliases for backward compatibility
    const isZoomed = isImageZoomed;

    /**
     * Triggers GTM event when lightbox is opened
     */
    const triggerLightboxOpenEvent = (): void => {
        if (!props.product) return;

        gtm?.trackEvent({
            event: 'product_lightbox_open',
            action: 'product_lightbox_open',
            product_id: props.product.id || '',
            product_name: props.product.translated?.name || '',
            image_count: props.product.media?.length || 0,
            initial_slide: props.initialSlide,
        });
    };

    /**
     * Closes the lightbox and resets all zoom states
     */
    const closeLightbox = (): void => {
        resetAllZoomStates();
        emit('isOpen');
    };

    /**
     * Handle lightbox-specific keydown events (like Escape)
     */
    const handleLightboxKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            closeLightbox();
        } else {
            // Delegate other key events to the zoom composable
            handleKeydown(event);
        }
    };

    /**
     * Handle slide change events and emit to parent
     */
    const onLightboxSlideChange = (): void => {
        onZoomSlideChange();
        if (mainSwiper.value) {
            emit('slideChange', mainSwiper.value.activeIndex);
        }
    };

    const onLightboxSlideChangeTransitionStart = () => {
        // Keep current state - will be restored in onLightboxSlideChange
    };

    // Focus management
    let previousActiveElement: Element | null = null;

    watch(
        () => props.isOpen,
        newValue => {
            if (newValue) {
                // Save current focus for restoration later
                previousActiveElement = document.activeElement;

                // Prevent browser zoom and page scrolling
                preventBrowserZoom();
                preventPageScrolling();

                // Trigger GTM tracking event
                triggerLightboxOpenEvent();

                nextTick(() => {
                    // Navigate to initial slide and restore its state
                    navigateToSlide(props.initialSlide);
                    restoreSlideZoomState(props.initialSlide);

                    // Focus the close button for accessibility
                    if (closeButton.value) {
                        closeButton.value.focus();
                    }
                });
            } else {
                // Restore browser and page settings
                restoreBrowserZoom();
                restorePageScrolling();
                resetAllZoomStates();

                // Restore previous focus
                nextTick(() => {
                    if (previousActiveElement && 'focus' in previousActiveElement) {
                        (previousActiveElement as HTMLElement).focus();
                    }
                });
            }
        },
    );

    watch(
        () => props.initialSlide,
        newSlide => {
            if (props.isOpen && mainSwiper.value) {
                nextTick(() => {
                    navigateToSlide(newSlide);
                    restoreSlideZoomState(newSlide);
                });
            }
        },
    );
</script>

<style scoped>
    :global(body.lightbox-open) {
        overflow: hidden !important;
        position: fixed !important;
        width: 100% !important;
        touch-action: manipulation;
        -ms-touch-action: manipulation;
        -webkit-user-select: none;
        user-select: none;
    }

    .pdp-lightbox-slider {
        touch-action: manipulation;
        user-scalable: no;
    }

    .zoom-container {
        touch-action: pan-x pan-y pinch-zoom;
    }

    .zoom-target {
        backface-visibility: hidden;
        transform3d: translate3d(0, 0, 0);
    }

    /* Mobile-specific optimizations */
    @media (max-width: 991px) {
        .zoom-container {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1rem;
        }

        .zoom-target {
            width: 100% !important;
            height: 100% !important;
            object-fit: contain;
        }

        .pdp-lightbox-slider .swiper-slide {
            height: 100% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
        }
    }
</style>
